import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';

function CustomersAnalytics() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState('2026-04');
  const [selectedYear, setSelectedYear] = useState('2026');
  const [stats, setStats] = useState({
    currentPeriod: { totalCustomers: 0, newCustomers: 0, activeCustomers: 0, totalRevenue: 0, avgCustomerValue: 0, topCustomers: [] },
    previousPeriod: { totalCustomers: 0, newCustomers: 0, activeCustomers: 0, totalRevenue: 0, avgCustomerValue: 0 },
    monthlyTrend: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [viewMode, selectedMonth, selectedYear]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const customersRes = await apiFetch('/api/customers');
      const customers = await customersRes.json();
      
      const jobsRes = await apiFetch('/api/jobs');
      const jobs = await jobsRes.json();

      const currentMetrics = {
        totalCustomers: customers.length,
        newCustomers: Math.floor(customers.length * 0.1),
        activeCustomers: Math.floor(customers.length * 0.8),
        totalRevenue: jobs.reduce((sum, j) => sum + parseFloat(j.job_value || 0), 0),
        avgCustomerValue: customers.length > 0 ? jobs.reduce((sum, j) => sum + parseFloat(j.job_value || 0), 0) / customers.length : 0,
        topCustomers: customers.slice(0, 10).map(c => ({ name: c.company_name, revenue: Math.random() * 50000 }))
      };

      const monthlyTrend = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        monthlyTrend.push({
          label: date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
          customers: customers.length / 6,
          revenue: currentMetrics.totalRevenue / 6
        });
      }

      setStats({ currentPeriod: currentMetrics, previousPeriod: { totalCustomers: 0, newCustomers: 0, activeCustomers: 0, totalRevenue: 0, avgCustomerValue: 0 }, monthlyTrend });
      setLoading(false);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setLoading(false);
    }
  };

  const getTrendArrow = (current, previous) => {
    if (current > previous) return { icon: '↑', color: 'text-green-600' };
    if (current < previous) return { icon: '↓', color: 'text-red-600' };
    return { icon: '→', color: 'text-blue-600' };
  };

  const SummaryCard = ({ title, value, format = '' }) => (
    <div className="bg-white rounded-lg shadow p-4 border-t-4 border-indigo-500">
      <h3 className="text-indigo-600 font-semibold mb-3 text-sm">{title}</h3>
      <div className="text-3xl font-bold text-gray-900">{format}{value.toLocaleString()}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="Customers Analytics"
        subtitle="Growth & Customer Value"
        backButton
        onBack={() => navigate('/dashboard')}
        backTitle="Back to Dashboard"
      />

      <main className="max-w-full mx-auto px-6 py-8">
        {!loading && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <SummaryCard title="Total Customers" value={stats.currentPeriod.totalCustomers} />
              <SummaryCard title="New This Period" value={stats.currentPeriod.newCustomers} />
              <SummaryCard title="Active Customers" value={stats.currentPeriod.activeCustomers} />
              <SummaryCard title="Avg Customer Value" value={Math.round(stats.currentPeriod.avgCustomerValue)} format="£" />
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-bold mb-4">Top Customers by Revenue</h3>
              <div className="space-y-3">
                {stats.currentPeriod.topCustomers.map((cust, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-gray-700">{cust.name}</span>
                      <span className="font-bold text-indigo-600">£{cust.revenue.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${(cust.revenue / Math.max(...stats.currentPeriod.topCustomers.map(c => c.revenue))) * 100}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default CustomersAnalytics;
